import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../config/axios';
import { track } from '../utils/analytics';
import { clearOfflineCaches } from '../config/serviceWorker';
import {
  getAccessToken,
  provisionalUser,
  getRefreshToken,
  setTokens,
  clearTokens,
  onAuthLogout,
} from '../config/authStore';
import { syncOnSignIn, syncOnRestore } from '../config/preferencesSync';

// Global auth state (mirrors SystemContext's shape): the current user, a
// loading flag for the initial /api/auth/me hydration, and the auth actions.
// Tokens themselves live at module scope in config/authStore.js so the axios
// interceptors can read them; this context only owns the React-visible user.
//
// The default value keeps useAuth() safe without a provider (e.g. isolated
// component tests): logged out, not loading, no-op actions.
const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  loading: false,
  register: async () => {},
  login: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
  updateDisplayName: async () => {},
  changePassword: async () => {},
  deleteAccount: async () => {},
  accountDeleted: false,
  claimHandle: async () => {},
  acknowledgeHandleNotice: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // A stored token boots the session PROVISIONALLY from its claims (issue 644):
  // the header renders its signed-in form and the handle prompt decides itself
  // at first paint, so nothing below moves when /me answers. /me replaces the
  // provisional shape whole; a dead session (401) drops it.
  const [user, setUser] = useState(() => provisionalUser());
  // True once this tab deleted its account (issue 782): a signed-in page then
  // sends the reader home, told, rather than to the sign-in page.
  const [accountDeleted, setAccountDeleted] = useState(false);
  // Only "loading" when there is a persisted session to hydrate.
  const [loading, setLoading] = useState(() => !!(getAccessToken() || getRefreshToken()));
  // True once this page signed someone in itself (qa-runs#153): the boot-time
  // session read below is then about the session that was replaced, and its
  // answer — the old account, or its 401 — must not undo the new sign-in.
  const signedIn = useRef(false);

  // Hydrate the session on mount: with a stored token, load /api/auth/me.
  // A 401 means the session is dead even after the axios layer's refresh
  // attempt — clear the tokens and the provisional user. Other failures
  // (network) keep the tokens, and the provisional session with them: the
  // reader is still signed in for what the offline cache can serve, and the
  // next request that reaches the server settles it.
  useEffect(() => {
    if (!getAccessToken() && !getRefreshToken()) return undefined;
    let cancelled = false;
    apiClient
      .get('/api/auth/me')
      .then((res) => {
        if (cancelled || signedIn.current) return;
        setUser(res.data);
        // Synced preferences (issue 291), rule 2: a restored session applies
        // the account's bag (last write from any device) — or seeds the
        // account from this device when no row exists yet. Fire-and-forget:
        // sync is best-effort and must never gate the session itself. The
        // bag rides on /me, so this is no extra request.
        syncOnRestore(res.data && res.data.preferences);
      })
      .catch((err) => {
        if (cancelled || signedIn.current) return;
        if (err.response?.status === 401) { clearTokens(); setUser(null); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The axios layer broadcasts when a token refresh fails mid-session — the
  // tokens are already cleared there; drop the user from React state too.
  useEffect(() => onAuthLogout(() => setUser(null)), []);

  // All auth endpoints return the same success payload:
  // { user, access_token, refresh_token, ... } (server/utils/authTokens.js).
  const applyAuthPayload = useCallback((data) => {
    signedIn.current = true;
    setTokens(data);
    setUser(data.user);
    setLoading(false);
    return data.user;
  }, []);

  // Synced preferences (issue 291), rule 1: an EXPLICIT sign-in in this
  // browser merges by key (a locally-set preference wins over the account's,
  // an unset one adopts it) and pushes the merged picture back up. Explicit
  // sign-in only — a restored session is rule 2 (syncOnRestore, above) —
  // and fire-and-forget, so a sync failure never fails the sign-in.
  const register = useCallback(async (email, password, accessKey) => {
    const res = await apiClient.post('/api/auth/register', { email, password, ...(accessKey ? { accessKey } : {}) });
    const user = applyAuthPayload(res.data);
    track('sign-up', { method: 'password' });
    syncOnSignIn();
    return user;
  }, [applyAuthPayload]);

  const login = useCallback(async (email, password) => {
    const res = await apiClient.post('/api/auth/login', { email, password });
    const user = applyAuthPayload(res.data);
    track('sign-in', { method: 'password' });
    syncOnSignIn();
    return user;
  }, [applyAuthPayload]);

  // A dev-only test-login link (issue 1512): the code from /test-login's
  // fragment, traded once for a session.
  const loginWithTestLink = useCallback(async (code) => {
    const res = await apiClient.post('/api/auth/test-login/exchange', { code });
    const user = applyAuthPayload(res.data);
    syncOnSignIn();
    return user;
  }, [applyAuthPayload]);

  // idToken is a Google Identity Services credential (ID token) — the backend
  // verifies it and issues our own tokens.
  // accessKey only when the site asked for it (key_required, issue 1229).
  const loginWithGoogle = useCallback(async (idToken, accessKey) => {
    const res = await apiClient.post('/api/auth/google', { idToken, ...(accessKey ? { accessKey } : {}) });
    const user = applyAuthPayload(res.data);
    // A brand-new account answers 201 (issue 1600), a returning one 200.
    if (res.status === 201) track('sign-up', { method: 'google' }); else track('sign-in', { method: 'google' });
    syncOnSignIn();
    return user;
  }, [applyAuthPayload]);

  // PUT /api/auth/me returns the updated public user — mirror it into state.
  // PUT not PATCH: nginx CORS allow-methods omits PATCH (blocked in-browser).
  const updateDisplayName = useCallback(async (displayName) => {
    const res = await apiClient.put('/api/auth/me', { display_name: displayName });
    setUser(res.data);
    return res.data;
  }, []);

  // The API revokes ALL refresh tokens on a password change and returns a
  // fresh pair for this session — store them or the next silent refresh dies.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await apiClient.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return applyAuthPayload(res.data);
  }, [applyAuthPayload]);

  // The handle (issue 500). Claiming answers the full auth payload — the
  // access token carries the handle as a claim for the VTT — so it is applied
  // like a sign-in. Acknowledging a revoke/assign notice clears it locally
  // too, so the banner goes at once.
  const claimHandle = useCallback(async (handle) => {
    const res = await apiClient.post('/api/users/me/handle', { handle });
    return applyAuthPayload(res.data);
  }, [applyAuthPayload]);

  const acknowledgeHandleNotice = useCallback(async () => {
    await apiClient.delete('/api/users/me/handle-notice');
    setUser((prev) => (prev ? { ...prev, handle_notice: null } : prev));
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await apiClient.get('/api/auth/me');
    setUser(res.data);
    return res.data;
  }, []);

  // Issue 782: the account and everything only its own goes; the session with
  // it. No logout call follows — the server has already removed every token.
  const deleteAccount = useCallback(async (credential) => {
    await apiClient.post('/api/auth/account/delete', { ...credential, confirm: 'delete my account' });
    clearTokens();
    setAccountDeleted(true);
    setUser(null);
    clearOfflineCaches();
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    // Log out locally first — revocation is best-effort (the endpoint is
    // idempotent and always 200s; a network failure must not trap the user
    // in a logged-in UI).
    clearTokens();
    setUser(null);
    // Do not leave the last reader's pages on a shared device (issue 312).
    // The worker never stores an authenticated response, so this is courtesy
    // rather than a privacy boundary — but it costs one message.
    clearOfflineCaches();
    if (refreshToken) {
      try {
        await apiClient.post('/api/auth/logout', { refreshToken });
      } catch (e) {
        /* best-effort server-side revoke */
      }
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        register,
        login,
        loginWithGoogle,
        loginWithTestLink,
        logout,
        updateDisplayName,
        changePassword,
        deleteAccount,
        accountDeleted,
        claimHandle,
        acknowledgeHandleNotice,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
